import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { McpStatus, MeResponse, PackDef, Project, Thread } from '../api';
import { api } from '../api';
import type { SettingsSection } from './SettingsModal';
import * as perf from '../utils/switch_perf';

export interface SidebarProps {
  projects: Project[];
  project: Project | null;
  onSelectProject: (id: string) => void;
  onNewProject: (name: string, pack: string) => void;
  /** Open the import-from-zip modal. Owned by the parent so the import flow
   *  can reach the project-list state on success. */
  onImportProject: () => void;
  /** Persist a renamed project. Parent owns optimistic update + rollback. */
  onRenameProject: (id: string, name: string) => Promise<void> | void;
  threads: Thread[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
  onNewThread: () => void;
  onArchiveThread: (id: string) => void;
  /** Persist a renamed thread title. */
  onRenameThread: (id: string, title: string) => Promise<void> | void;
  /** Open the global Settings modal. Owned by the parent so the modal can
   *  reach project state (rename / pack swap / delete). The optional
   *  `section` arg lets each entry-point land on the most relevant tab —
   *  avatar → 'account', SETTINGS row → 'general' (default), MCP chip →
   *  'integrations'. */
  onOpenSettings: (section?: SettingsSection) => void;
  /** PR-10: authenticated user details for the footer chip. Null while
   *  /api/me is in flight (Sidebar still renders — chip is hidden). */
  me: MeResponse | null;
  /** Open the unified admin page (Overview / Usage / Errors / Users).
   *  App.tsx renders the link only when `me.is_admin` is true; the
   *  require_admin server check on each endpoint is the actual
   *  authorisation boundary. Null means hide the link. */
  onOpenAdmin?: (() => void) | null;
  /** Per-thread "currently inferencing" flags. Threads still streaming on
   *  *other* projects also live in this map (App.tsx never clears them on
   *  project switch), so a busy thread you've navigated away from still
   *  signals via the project row dot. */
  busyByThread?: Record<string, boolean>;
  /** Set of project ids that have at least one thread currently busy.
   *  Pre-computed by App.tsx so the sidebar doesn't need to know the
   *  thread→project mapping for off-screen threads. */
  busyProjects?: ReadonlySet<string>;
  /** Per-thread "you have unread messages here" — count or 0. The active
   *  thread is always 0 (you're looking at it; cursor is bumped on view).
   *  Phase 2: populated from the project SSE `thread_message_added`
   *  events vs. last-read cursor. */
  unreadByThread?: Record<string, number>;
  /** Set of project ids with at least one thread carrying unread > 0,
   *  computed by App.tsx for the same reason as `busyProjects`. */
  unreadProjects?: ReadonlySet<string>;
}

/** Tiny status chip rendered next to the active project row, shown ONLY
 *  when MCP is enabled AND has had recent read activity. The "○ setup" /
 *  "⊘ off" states render nothing — they were just visual noise for the
 *  silent majority who don't use external MCP, and the same info is one
 *  click away in Settings → Integrations.
 *
 *  Click jumps directly to Settings → Integrations (not General, the
 *  modal's default), so the chip's only meaningful click target stays
 *  focused on what's relevant. */
function McpChip({
  projectId,
  onClick,
}: {
  projectId: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<McpStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.getMcpStatus(projectId)
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Silent majority: don't render until we have a confirmed "MCP is
  // actively being used" signal. Includes the loading state — nothing
  // worse than a chip that flashes "MCP · setup" for 200ms then hides.
  if (!status || !status.enabled || status.recent_count_24h <= 0) {
    return null;
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="mono"
      title={t('sidebar.mcpChipTitle')}
      style={{
        fontSize: 9.5,
        padding: '1px 6px 2px',
        marginLeft: 8,
        border: 'none',
        background: 'transparent',
        color: 'var(--ink-1)',
        letterSpacing: '.04em',
        cursor: 'pointer',
        textAlign: 'left',
        fontWeight: 500,
      }}
    >
      {t('sidebar.mcpChip', { count: status.recent_count_24h })}
    </button>
  );
}

/** Status dot rendered to the LEFT of a project / thread row name.
 *  - busy: animated breathing blue dot ("inference is ongoing")
 *  - unread: static gray dot ("messages arrived since you last viewed")
 *  - both: busy wins (the unread state will collapse to "read" the
 *    moment you switch in, so showing the more informative live state
 *    is the right call)
 *  Returns null when neither — caller still pays for the slot via the
 *  fixed-width `StatusSlot` so layout doesn't shift on/off.
 */
function StatusDot({
  busy,
  unread,
}: {
  busy: boolean;
  unread: boolean;
}) {
  if (!busy && !unread) return null;
  return (
    <span
      aria-hidden="true"
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: busy ? 'var(--blue)' : 'var(--ink-3)',
        flexShrink: 0,
        animation: busy ? 'draftlizeSidebarPulse 1.2s ease-in-out infinite' : undefined,
      }}
    />
  );
}

/** Fixed-width slot before a row's text. Keeps the title baseline aligned
 *  whether the dot is showing or not. 10px = 6px dot + 4px gap. */
function StatusSlot({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        width: 10,
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

export function Sidebar({
  projects, project, onSelectProject, onNewProject, onImportProject, onRenameProject,
  threads, activeThreadId, onSelectThread, onNewThread, onArchiveThread, onRenameThread,
  onOpenSettings, me, onOpenAdmin,
  busyByThread, busyProjects, unreadByThread, unreadProjects,
}: SidebarProps) {
  const { t } = useTranslation();
  // Inject the breathing-dot keyframe once on mount. Sidebar is a singleton
  // in the live tree so this is effectively module-scope; declaring it here
  // keeps the visual dependency colocated with the component that uses it.
  useEffect(() => {
    const ID = 'draftlize-sidebar-pulse-keyframes';
    if (document.getElementById(ID)) return;
    const styleEl = document.createElement('style');
    styleEl.id = ID;
    styleEl.textContent = `
@keyframes draftlizeSidebarPulse {
  0%   { opacity: 0.35; }
  50%  { opacity: 1;    }
  100% { opacity: 0.35; }
}
`;
    document.head.appendChild(styleEl);
    return () => {
      // Sidebar is mounted for the lifetime of the app; teardown is mostly
      // for hot-reload sanity. Removing the rule on a real unmount is safe
      // because StatusDot only references the keyframe by name when busy.
      styleEl.remove();
    };
  }, []);
  perf.renderMark('Sidebar', { projects: projects.length, threads: threads.length });
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [hoverProjectId, setHoverProjectId] = useState<string | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectPack, setNewProjectPack] = useState('blank');
  const [packs, setPacks] = useState<PackDef[]>([]);
  // `+ NEW` opens a tiny dropdown to choose between an empty project and
  // the import-from-zip flow. Each option then drives its own UI (inline
  // form vs. modal). Closes on outside click or Escape — both wired below.
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuAnchorRef = useRef<HTMLDivElement | null>(null);
  const newProjectInputRef = useRef<HTMLInputElement | null>(null);
  // Inline rename state — only one row at a time. Tracks {kind, id, draft}.
  // null = nothing being renamed. Submit on Enter / blur, cancel on Esc.
  const [renaming, setRenaming] = useState<
    | { kind: 'project'; id: string; draft: string }
    | { kind: 'thread'; id: string; draft: string }
    | null
  >(null);
  // Persist collapsed state across reloads — losing it would force the user
  // to re-collapse every refresh, which defeats the "I want max canvas" intent.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('draftlize.sidebarCollapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('draftlize.sidebarCollapsed', collapsed ? '1' : '0'); }
    catch { /* private mode — silently degrade */ }
  }, [collapsed]);

  // Pack list is small and stable — fetch once on mount, ignore failures
  // (the create dialog falls back to a single 'blank' option).
  useEffect(() => {
    api.listPacks().then(setPacks).catch(() => {});
  }, []);

  useEffect(() => {
    if (creatingProject) newProjectInputRef.current?.focus();
  }, [creatingProject]);

  // Outside-click + Escape to close the `+ NEW` dropdown. Re-binding only
  // when the menu is open keeps the global listener noise out of normal use.
  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!newMenuAnchorRef.current) return;
      if (!newMenuAnchorRef.current.contains(e.target as Node)) {
        setNewMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNewMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [newMenuOpen]);

  const submitNewProject = () => {
    const name = newProjectName.trim();
    if (!name) {
      setCreatingProject(false);
      return;
    }
    onNewProject(name, newProjectPack);
    setNewProjectName('');
    setNewProjectPack('blank');
    setCreatingProject(false);
  };
  const cancelNewProject = () => {
    setNewProjectName('');
    setNewProjectPack('blank');
    setCreatingProject(false);
  };

  if (collapsed) {
    return (
      <aside
        onClick={() => setCollapsed(false)}
        title={t('sidebar.expand')}
        style={{
          width: 32,
          flexShrink: 0,
          background: 'var(--paper-1)',
          borderRight: '1px solid var(--rule)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            height: 45,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid var(--rule)',
            fontSize: 12,
            color: 'var(--ink-3)',
            fontWeight: 700,
          }}
          className="mono"
        >
          ▶
        </div>
        <div
          style={{
            width: 22, height: 22, borderRadius: 5,
            background: 'var(--ink-0)', color: 'white',
            display: 'grid', placeItems: 'center',
            fontSize: 12, fontWeight: 700,
            marginTop: 12,
          }}
        >
          D
        </div>
      </aside>
    );
  }

  return (
    <aside
      style={{
        width: 220,
        background: 'var(--paper-1)',
        borderRight: '1px solid var(--rule)',
        flexShrink: 0,
        fontSize: 13,
        overflowY: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 45,
          padding: '0 14px 0 10px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 22, height: 22, borderRadius: 5,
            background: 'var(--ink-0)', color: 'white',
            display: 'grid', placeItems: 'center',
            fontSize: 12, fontWeight: 700,
            flexShrink: 0,
          }}
        >
          D
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.15 }}>Draftlize</div>
          <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', letterSpacing: '.08em', lineHeight: 1.2 }}>
            {t('sidebar.tagline')}
          </div>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          title={t('sidebar.collapse')}
          className="mono"
          style={{
            fontSize: 11,
            padding: '2px 6px',
            color: 'var(--ink-3)',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          ◂
        </button>
      </div>

      <div
        style={{
          padding: '4px 10px 14px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div
        className="mono"
        style={{
          fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em',
          padding: '10px 6px 4px', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {t('sidebar.projects')}
        <span style={{ flex: 1 }} />
        <div ref={newMenuAnchorRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setNewMenuOpen((v) => !v)}
            className="mono"
            style={{
              fontSize: 10,
              padding: '2px 6px',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              color: 'var(--ink-1)',
              background: newMenuOpen ? 'var(--paper-1)' : 'white',
            }}
          >
            {t('sidebar.newMenu')} ▾
          </button>
          {newMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                zIndex: 50,
                background: 'white',
                border: '1px solid var(--rule)',
                borderRadius: 5,
                boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                padding: 4,
                minWidth: 168,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <button
                onClick={() => {
                  setNewMenuOpen(false);
                  setCreatingProject(true);
                }}
                className="mono"
                style={{
                  textAlign: 'left',
                  fontSize: 11,
                  padding: '6px 8px',
                  borderRadius: 3,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--paper-1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                {t('sidebar.newEmptyProject')}
              </button>
              <button
                onClick={() => {
                  setNewMenuOpen(false);
                  onImportProject();
                }}
                className="mono"
                style={{
                  textAlign: 'left',
                  fontSize: 11,
                  padding: '6px 8px',
                  borderRadius: 3,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--paper-1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                ↥ {t('sidebar.importFromZip')}
              </button>
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '2px 0' }}>
        {creatingProject && (
          <div style={{ padding: '4px 8px' }}>
            <input
              ref={newProjectInputRef}
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitNewProject();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelNewProject();
                }
              }}
              placeholder={t('sidebar.projectNamePlaceholder')}
              style={{
                width: '100%',
                fontSize: 12.5,
                padding: '4px 6px',
                border: '1px solid var(--blue)',
                borderRadius: 3,
                background: 'white',
                color: 'var(--ink-1)',
                boxSizing: 'border-box',
              }}
            />
            {packs.length > 1 && (
              <select
                value={newProjectPack}
                onChange={(e) => setNewProjectPack(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitNewProject();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelNewProject();
                  }
                }}
                style={{
                  width: '100%',
                  fontSize: 11.5,
                  padding: '3px 4px',
                  marginTop: 4,
                  border: '1px solid var(--rule)',
                  borderRadius: 3,
                  background: 'white',
                  color: 'var(--ink-1)',
                  boxSizing: 'border-box',
                }}
                title={packs.find((p) => p.id === newProjectPack)?.description ?? ''}
              >
                {packs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.description}
                  </option>
                ))}
              </select>
            )}
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              <button
                onClick={submitNewProject}
                className="mono"
                style={{
                  flex: 1,
                  fontSize: 10,
                  padding: '3px 6px',
                  border: '1px solid var(--blue)',
                  borderRadius: 3,
                  background: 'var(--blue)',
                  color: 'white',
                }}
              >
                {t('sidebar.create')}
              </button>
              <button
                onClick={cancelNewProject}
                className="mono"
                style={{
                  fontSize: 10,
                  padding: '3px 8px',
                  border: '1px solid var(--rule)',
                  borderRadius: 3,
                  color: 'var(--ink-2)',
                  background: 'transparent',
                }}
              >
                ESC
              </button>
            </div>
          </div>
        )}
        {projects.length === 0 && !creatingProject && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '6px 8px', fontStyle: 'italic' }}>
            {t('common.loading')}
          </div>
        )}
        {projects.map((p) => {
          const isActive = p.id === project?.id;
          const isHover = p.id === hoverProjectId;
          const isRenaming = renaming?.kind === 'project' && renaming.id === p.id;
          // Aggregate signal for off-screen project state. The active
          // project's own activity shows on its thread rows below — but
          // a project you've navigated AWAY from has no thread list
          // visible, so this dot is the only way the user sees that
          // their slow inference (or unread reply) is still pending.
          const projectBusy = !isActive && (busyProjects?.has(p.id) ?? false);
          const projectUnread = !isActive && (unreadProjects?.has(p.id) ?? false);
          return (
            <div
              key={p.id}
              onMouseEnter={() => setHoverProjectId(p.id)}
              onMouseLeave={() => setHoverProjectId(null)}
              onClick={() => {
                if (isRenaming) return;
                perf.start(p.id, { from: project?.id ?? null, to: p.id, name: p.name });
                onSelectProject(p.id);
              }}
              onDoubleClick={(e) => {
                // Open the inline rename input. Stop propagation so the
                // single-click select handler doesn't also fire (and so the
                // browser doesn't accidentally start a text selection in
                // the row's text node).
                e.stopPropagation();
                setRenaming({ kind: 'project', id: p.id, draft: p.name });
              }}
              title={isRenaming ? undefined : t('sidebar.doubleClickRename')}
              style={{
                padding: isRenaming ? '3px 6px' : '6px 8px',
                marginBottom: 1,
                background: isActive ? 'white' : (isHover ? 'rgba(0,0,0,0.03)' : 'transparent'),
                borderLeft: isActive ? '2px solid var(--blue)' : '2px solid transparent',
                cursor: isRenaming ? 'text' : 'pointer',
                fontSize: 12.5,
                color: 'var(--ink-1)',
                fontWeight: isActive ? 500 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isRenaming ? (
                <RenameInput
                  draft={renaming.draft}
                  onChange={(v) => setRenaming({ kind: 'project', id: p.id, draft: v })}
                  onCommit={async () => {
                    const next = renaming.draft.trim();
                    setRenaming(null);
                    if (next && next !== p.name) {
                      await onRenameProject(p.id, next);
                    }
                  }}
                  onCancel={() => setRenaming(null)}
                />
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <StatusSlot>
                    <StatusDot busy={projectBusy} unread={projectUnread} />
                  </StatusSlot>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.name}
                  </span>
                  {/* Connection chip is only meaningful for the project the
                      user is currently working in — the others' MCP state
                      isn't actionable from the row. Click jumps directly
                      to the Integrations tab (the chip's only relevant
                      destination). */}
                  {isActive && (
                    <McpChip projectId={p.id} onClick={() => onOpenSettings('integrations')} />
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="mono"
        style={{
          fontSize: 10, color: 'var(--ink-3)', letterSpacing: '.1em',
          padding: '14px 6px 4px', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {t('sidebar.threads')}
        <span style={{ flex: 1 }} />
        <button
          onClick={onNewThread}
          className="mono"
          style={{
            fontSize: 10, padding: '2px 6px',
            border: '1px solid var(--rule)', borderRadius: 3,
            color: 'var(--ink-1)',
          }}
        >
          {t('sidebar.newThread')}
        </button>
      </div>
      <div style={{ padding: '2px 0', flex: 1, overflowY: 'auto' }}>
        {threads.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '6px 8px', fontStyle: 'italic' }}>
            {t('sidebar.noThreads')}
          </div>
        )}
        {threads.map(thread => {
          const isActive = thread.id === activeThreadId;
          const isHover = thread.id === hoverId;
          const isRenaming = renaming?.kind === 'thread' && renaming.id === thread.id;
          // Active thread is never "unread" — viewing it implies the cursor
          // is being bumped (Phase 2 wires this through to the server).
          // Busy state is shown for ALL threads including active, so users
          // who scrolled away from the live composer can still see "yes,
          // this thread is generating" at the sidebar level.
          const threadBusy = !!busyByThread?.[thread.id];
          const threadUnread = !isActive && (unreadByThread?.[thread.id] ?? 0) > 0;
          return (
            <div
              key={thread.id}
              onMouseEnter={() => setHoverId(thread.id)}
              onMouseLeave={() => setHoverId(null)}
              onClick={() => { if (!isRenaming) onSelectThread(thread.id); }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setRenaming({ kind: 'thread', id: thread.id, draft: thread.title ?? '' });
              }}
              title={isRenaming ? undefined : t('sidebar.doubleClickRename')}
              style={{
                padding: isRenaming ? '3px 6px' : '6px 8px',
                marginBottom: 1,
                background: isActive ? 'white' : (isHover ? 'rgba(0,0,0,0.03)' : 'transparent'),
                borderLeft: isActive ? '2px solid var(--blue)' : '2px solid transparent',
                cursor: isRenaming ? 'text' : 'pointer',
                fontSize: 12.5,
                color: 'var(--ink-1)',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ fontWeight: (isActive || threadUnread) ? 500 : 400, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {isRenaming ? (
                    <RenameInput
                      draft={renaming.draft}
                      onChange={(v) => setRenaming({ kind: 'thread', id: thread.id, draft: v })}
                      onCommit={async () => {
                        const next = renaming.draft.trim();
                        setRenaming(null);
                        if (next && next !== (thread.title ?? '')) {
                          await onRenameThread(thread.id, next);
                        }
                      }}
                      onCancel={() => setRenaming(null)}
                    />
                  ) : (
                    <>
                      <StatusSlot>
                        <StatusDot busy={threadBusy} unread={threadUnread} />
                      </StatusSlot>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <DiscussGlyphTitle title={thread.title} />
                      </span>
                    </>
                  )}
                </div>
                {isHover && !isRenaming && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchiveThread(thread.id);
                    }}
                    className="mono"
                    title={t('sidebar.archiveThread')}
                    style={{
                      fontSize: 11,
                      lineHeight: 1,
                      padding: '2px 6px',
                      border: '1px solid var(--rule)',
                      borderRadius: 3,
                      color: 'var(--ink-3)',
                      background: 'white',
                      flexShrink: 0,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', marginTop: 1 }}>
                {new Date(thread.updated_at).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {/* Single 48px footer band: avatar (→ Account) · SETTINGS (→ General) · ADMIN.
          Replaces the previous two-row layout (separate 41px sign-out row + 48px
          settings row) — sign-out now lives in Settings → Account, freeing up
          vertical real estate without losing any controls.
          The canonical 48px height is shared with CardsPanel / GapLedger / composer
          so the bottom edge stays flush across panes. */}
      <div
        className="mono"
        style={{
          borderTop: '1px solid var(--rule)',
          height: 48,
          boxSizing: 'border-box',
          padding: '0 10px 0 12px',
          fontSize: 11,
          color: 'var(--ink-2)',
          letterSpacing: '.06em',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        {/* Avatar — opens Settings on the Account tab. The previous design
            had a full text row showing @username + a SIGN OUT button. The
            avatar communicates the same identity at 22px (vs ~41px), and
            the sign-out lives one click away on the same Account tab. */}
        {me && (
          <button
            type="button"
            onClick={() => onOpenSettings('account')}
            title={
              me.email
                ? t('sidebar.openAccountTitle', {
                    identity: `${me.github_login ? '@' + me.github_login + ' · ' : ''}${me.email}`,
                  })
                : me.github_login
                  ? t('sidebar.openAccountTitle', { identity: `@${me.github_login}` })
                  : t('sidebar.openAccount')
            }
            aria-label={t('sidebar.openAccountAria')}
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              background: 'var(--paper-2)',
              color: 'var(--ink-1)',
              display: 'grid',
              placeItems: 'center',
              fontSize: 11,
              fontWeight: 700,
              border: '1px solid var(--rule)',
              cursor: 'pointer',
              flexShrink: 0,
              padding: 0,
            }}
          >
            {(me.display_name || me.github_login || me.email || '?').charAt(0).toUpperCase()}
          </button>
        )}
        {/* 计费重构：钱包余额常驻 chip。点击进 Settings → Billing 充值。
            余额偏低（< $1）时染成 amber 提醒。 */}
        {me && (
          <button
            type="button"
            onClick={() => onOpenSettings('billing')}
            title={t('billing.balance')}
            className="mono"
            style={{
              border: '1px solid var(--rule)',
              background: me.wallet?.low_balance ? 'var(--amber-soft)' : 'var(--paper-2)',
              color: me.wallet?.low_balance ? 'var(--amber)' : 'var(--ink-1)',
              borderRadius: 4,
              padding: '3px 7px',
              fontSize: 10.5,
              letterSpacing: '.04em',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ${((me.wallet?.balance_cents ?? 0) / 100).toFixed(2)}
          </button>
        )}
        <div
          onClick={() => onOpenSettings('general')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            flex: 1,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.color = 'var(--ink-1)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.color = '';
          }}
        >
          <span style={{ fontSize: 13 }}>⚙</span>
          <span>{t('sidebar.settings')}</span>
        </div>
        {onOpenAdmin && (
          <button
            type="button"
            onClick={onOpenAdmin}
            className="mono"
            title={t('sidebar.adminTitle')}
            style={{
              fontSize: 10,
              padding: '2px 6px',
              border: '1px solid var(--rule-s)',
              background: 'transparent',
              color: 'var(--ink-3)',
              borderRadius: 3,
              letterSpacing: '.06em',
              cursor: 'pointer',
            }}
          >
            {t('sidebar.admin')}
          </button>
        )}
      </div>
    </aside>
  );
}

/**
 * Render a thread title with a small ✦ amber glyph when it was spawned via
 * the Discuss-on-gap flow (App's onDiscussProposal sets the title to
 * `Discuss · …`). The "Discuss · " prefix is stripped from the visible
 * text — the glyph carries the role, the rest is just the gap subject.
 * If the user manually renames the thread, the prefix vanishes and the
 * glyph with it; that's the implicit contract of renaming.
 */
function DiscussGlyphTitle({ title }: { title: string | null }) {
  const { t } = useTranslation();
  const text = title || t('sidebar.untitled');
  const PREFIX = 'Discuss · ';
  if (text.startsWith(PREFIX)) {
    return (
      <>
        <span style={{ color: 'var(--amber)', marginRight: 4 }}>✦</span>
        {text.slice(PREFIX.length)}
      </>
    );
  }
  return <>{text}</>;
}

/**
 * Single-line input that auto-focuses + selects on mount, commits on Enter
 * or blur, cancels on Esc. Used for both project and thread inline rename.
 * Empty/unchanged input is a no-op (the parent's onCommit short-circuits).
 */
function RenameInput({
  draft,
  onChange,
  onCommit,
  onCancel,
}: {
  draft: string;
  onChange: (v: string) => void;
  onCommit: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);
  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => { void onCommit(); }}
      onKeyDown={(e) => {
        // Skip while IME composing — Enter finalizes the glyph, not the rename.
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          void onCommit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      style={{
        width: '100%',
        fontSize: 12.5,
        padding: '3px 5px',
        border: '1px solid var(--blue)',
        borderRadius: 3,
        background: 'white',
        color: 'var(--ink-1)',
        boxSizing: 'border-box',
        outline: 'none',
      }}
    />
  );
}
